import React, { useEffect, useState, useRef, useMemo } from "react";
import { useDispatch } from 'react-redux';
import "./../../css/css/message_chat.css";
import ChatMessage from "./ChatMessage";
import moment from "moment";
import LottieLoader from "../LottieLoader";
import lottieEmptyFile from '../../files/animation/empty_new.json';
import _ from "lodash";
import { sendSocketMessage } from '../ChatTextMessage/socket.js';

const ChatTextMessage = (props) => {
  const dispatch = useDispatch();
  const { patientId, isLoadingChatList, allChatMessages } = props;
  // Keep unique messages, then sort oldest -> latest so newest stays at bottom.
  const allChatMessagesUpdated = useMemo(() => {
    return _.orderBy(
      _.uniqBy(allChatMessages || [], "_id"),
      [(item) => new Date(item.createdAt || 0).getTime()],
      ["asc"]
    );
  }, [allChatMessages]);
  const [message, setMessage] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredMessages, setFilteredMessages] = useState([]); // State to hold filtered messages
  const [optimisticMessages, setOptimisticMessages] = useState([]);
  const [activeStickyDate, setActiveStickyDate] = useState("");
  const chatMessagesRef = useRef(null);
  const messageInputRef = useRef(null);
  const separatorRefs = useRef([]);
  const activeStickyDateRef = useRef("");
  const scrollRafRef = useRef(null);

  const getSenderId = (msg) => String(msg?.sender?._id || msg?.sender || "");
  const mergedChatMessages = useMemo(() => {
    return _.orderBy(
      [...allChatMessagesUpdated, ...optimisticMessages],
      [(item) => new Date(item.createdAt || 0).getTime()],
      ["asc"]
    );
  }, [allChatMessagesUpdated, optimisticMessages]);

  const messagesToRender = filteredMessages.length > 0 ? filteredMessages : mergedChatMessages || [];
  const totalMessages = mergedChatMessages.length;
  const lastMessageTime = totalMessages > 0 ? mergedChatMessages[totalMessages - 1]?.createdAt : null;

  const getFormattedDateLabel = (createdAt) => {
    if (!createdAt) return "Unknown date";
    const msgMoment = moment(createdAt);
    if (msgMoment.isSame(moment(), "day")) return "Today";
    if (msgMoment.isSame(moment().subtract(1, "day"), "day")) return "Yesterday";
    return msgMoment.format("MMMM DD, YYYY");
  };

  const updateStickyDate = () => {
    const container = chatMessagesRef.current;
    if (!container) return;
    const separators = separatorRefs.current.filter(Boolean);
    if (!separators.length) {
      setActiveStickyDate("");
      return;
    }
    const threshold = container.scrollTop + 14;
    let label = separators[0]?.dataset?.dateLabel || "";
    separators.forEach((el) => {
      if (el.offsetTop <= threshold && el.dataset?.dateLabel) {
        label = el.dataset.dateLabel;
      }
    });
    if (activeStickyDateRef.current !== label) {
      activeStickyDateRef.current = label;
      setActiveStickyDate(label);
    }
  };

  const onChatScroll = () => {
    if (scrollRafRef.current) {
      return;
    }
    scrollRafRef.current = requestAnimationFrame(() => {
      updateStickyDate();
      scrollRafRef.current = null;
    });
  };

  const renderMessagesWithDateSeparator = (messages = []) => {
    separatorRefs.current = [];
    let lastDate = null;
    let separatorIndex = -1;
    return messages.map((model) => {
      const messageDate = model?.createdAt ? moment(model.createdAt).format("YYYY-MM-DD") : "unknown";
      const showDateSeparator = messageDate !== lastDate;
      lastDate = messageDate;
      const formattedDate = getFormattedDateLabel(model?.createdAt);
      if (showDateSeparator) {
        separatorIndex += 1;
      }
      const currentSeparatorIndex = separatorIndex;

      return (
        <React.Fragment key={model._id}>
          {showDateSeparator && (
            <div
              className="chat-date-separator"
              data-date-label={formattedDate}
              ref={(el) => {
                if (el) {
                  separatorRefs.current[currentSeparatorIndex] = el;
                }
              }}
            >
              <span>{formattedDate}</span>
            </div>
          )}
          <ChatMessage
            isSender={String(props.userId) === String(model?.sender?._id || model?.sender)}
            dataModel={model}
            name={model.sender && model.sender.fname + " " + model.sender.lname}
            image={model.sender && model.sender.profileImage}
            isOptimistic={Boolean(model.__optimistic)}
          />
        </React.Fragment>
      );
    });
  };
  // Logic to change section    
  // useEffect(() => {
  //   onReceiveMessage((data) => {
  //     const objectExists = _.some(allChatMessagesUpdated, { _id: data._id });
  //     if (!objectExists) {
  //       //console.log("RECEIVED MESSAGES :" + JSON.stringify(data));
  //       dispatch({
  //         type: "DO_CHAT_MESSAGE",
  //         payload: data
  //       });
  //     } else {
  //       console.log("RECEIVED MESSAGES IS EXIST IN ARRAY");
  //     }
  //   });   
  // }, [dispatch]);

  // Function to scroll chat messages to the bottom
  const scrollToBottom = () => {
    if (chatMessagesRef.current) {
      chatMessagesRef.current.scrollTo({
        top: chatMessagesRef.current.scrollHeight,
        behavior: "smooth" // Use smooth behavior for scroll animation
      });
    }
  };

  // Effect hook to fetch initial data and unread count on component mount
  useEffect(() => {
    dispatch({
      type: "DO_GET_UNREAD_COUNT"
    });
    fetchData();
  }, [dispatch]);

  // Effect hook to scroll to bottom when chat messages update
  useEffect(() => {
    scrollToBottom();
  }, [mergedChatMessages]);

  useEffect(() => {
    if (!optimisticMessages.length) {
      return;
    }
    setOptimisticMessages((prev) =>
      {
        const next = prev.filter((localMsg) => {
        return !allChatMessagesUpdated.some((serverMsg) => {
          const sameSender = getSenderId(serverMsg) === String(props.userId);
          const sameBody = (serverMsg.body || "").trim() === (localMsg.body || "").trim();
          const timeGap = Math.abs(
            new Date(serverMsg.createdAt || 0).getTime() - new Date(localMsg.createdAt || 0).getTime()
          );
          return sameSender && sameBody && timeGap <= 10000;
        });
      });
        return next.length === prev.length ? prev : next;
      }
    );
  }, [allChatMessagesUpdated, optimisticMessages.length, props.userId]);

  // Function to fetch chat messages for a specific patient
  const fetchData = () => {
    if (patientId) {
      dispatch({
        type: "DO_GET_ALL_CHAT_MESSAGES",
        payload: {
          patient: patientId,
          tz: moment.tz.guess()
        }
      });
    }
  };

  // Function to handle input change in search field
  const handleChangeSearch = (e) => {
    setSearchTerm(e.target.value);
    if (e.target.value.trim() !== '') {
      // Implement your search logic here, for example:
      // This will filter messages containing the searchTerm
      // Case insensitive search
      const filteredMessages = mergedChatMessages.filter((message) =>
        (message.body || "").toLowerCase().includes(e.target.value.toLowerCase())
      );
      setFilteredMessages(filteredMessages); // Update state with filtered messages
    } else {
      const filteredMessages  = mergedChatMessages;
      setFilteredMessages(filteredMessages);
    }

  };

  // Effect hook to scroll to bottom after rendering messages or search results
  useEffect(() => {
    const timeout = setTimeout(() => {
      scrollToBottom();
      updateStickyDate();
    }, 100); // Adjust timeout delay as needed

    return () => clearTimeout(timeout);
  }, [mergedChatMessages, filteredMessages]); // Trigger scroll when messages or search results update

  useEffect(() => {
    if (messagesToRender.length > 0) {
      const frame = requestAnimationFrame(() => updateStickyDate());
      return () => cancelAnimationFrame(frame);
    }
    activeStickyDateRef.current = "";
    setActiveStickyDate("");
    return undefined;
  }, [messagesToRender]);

  useEffect(() => {
    return () => {
      if (scrollRafRef.current) {
        cancelAnimationFrame(scrollRafRef.current);
      }
    };
  }, []);

  // Function to clear search and display all messages
  const clearSearch = () => {
    setSearchTerm('');
    setFilteredMessages([]);
  };

  // Function to handle sending a message
  const onFormSubmit = e => {
    e.preventDefault();
    const trimmedMessage = message.trim();
    if (trimmedMessage !== '') {
      const optimisticMessage = {
        _id: `tmp-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        body: trimmedMessage,
        createdAt: new Date().toISOString(),
        sender: {
          _id: props.userId,
          fname: "You",
          lname: ""
        },
        __optimistic: true
      };
      setOptimisticMessages((prev) => [...prev, optimisticMessage]);
      dispatch({
        type: "DO_SEND_CHAT_MESSAGE",
        payload: {
          patientId: patientId,
          //senderId: props.userId,
          message: trimmedMessage
        }
      });
      setMessage('');
      sendSocketMessage(trimmedMessage, patientId, props.userId);
      if (messageInputRef.current) {
        messageInputRef.current.style.height = "40px";
      }
    }
  }

  const handleMessageChange = (e) => {
    setMessage(e.target.value);
    if (messageInputRef.current) {
      messageInputRef.current.style.height = "40px";
      messageInputRef.current.style.height = `${Math.min(messageInputRef.current.scrollHeight, 120)}px`;
    }
  };

  const handleMessageKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      onFormSubmit(e);
    }
  };
  return (
    <div className="col-12 p-0 chat-section-shell">
      <div className="chat-section-header">
        <div className="chat-section-header-left">
          <div className="chat-section-title-row">
            <i className="mdi mdi-forum chat-section-icon" />
            <h5 className="chat-section-title">Message Chat</h5>
          </div>
          <p className="chat-section-subtitle">
            View conversation history and send secure updates to the patient.
          </p>
        </div>
        <div className="chat-section-header-right">
          <div className="chat-stat-card">
            <span className="chat-stat-label">Messages</span>
            <span className="chat-stat-value">{totalMessages}</span>
          </div>
          <div className="chat-stat-card">
            <span className="chat-stat-label">Last activity</span>
            <span className="chat-stat-value small">
              {lastMessageTime ? moment(lastMessageTime).format("MMM DD, YYYY HH:mm") : "-"}
            </span>
          </div>
        </div>
      </div>

      {!isLoadingChatList && mergedChatMessages && mergedChatMessages.length > 0 && (
        <>
          <div className="chat-control-bar">
            <div className="chat-input-group">
              <span className="chat-input-icon">
                <i className="mdi mdi-magnify" />
              </span>
              <input
                type="text"
                className="form-control input-date-filter remove-bottom-margin chat-control-input"
                placeholder="Search messages"
                value={searchTerm}
                onChange={handleChangeSearch}
              />
              {searchTerm !== '' && (
                <button type="button" className="btn-primary clear-search-btn" onClick={clearSearch}>
                  <i className="mdi mdi-close" style={{ fontSize: 18 }} />Clear
                </button>
              )}
            </div>
          </div>
        </>
      )}
      <div className="position-relative">
        <div className="chat-messages p-4" ref={chatMessagesRef} onScroll={onChatScroll}>
          {!isLoadingChatList && activeStickyDate && (
            <div className="chat-sticky-date-current">
              <span>{activeStickyDate}</span>
            </div>
          )}
          {isLoadingChatList && (
            <div className="chat-shimmer-wrapper" aria-label="Loading messages">
              {[1, 2, 3, 4].map((idx) => (
                <div
                  key={`chat-shimmer-${idx}`}
                  className={`chat-shimmer-row ${idx % 2 === 0 ? "right" : "left"}`}
                >
                  <div className="chat-shimmer-avatar shimmer" />
                  <div className="chat-shimmer-bubble shimmer" />
                </div>
              ))}
            </div>
          )}
          {!isLoadingChatList && (!mergedChatMessages || _.size(mergedChatMessages) === 0) && (
            <div className="text-center py-5">
              <LottieLoader
                loadLoop={false}
                autoplayLoop={true}
                source={lottieEmptyFile}
                Lheight={300}
                Lwidth={300}
                title={"Sorry! No message found :("}
                subTitle={"We can't find any messages."}
              />
            </div>
          )}
          {!isLoadingChatList && mergedChatMessages && mergedChatMessages.length > 0 && (
            <>
              {searchTerm !== '' && filteredMessages.length === 0 ? (
                <div className="text-center py-5">
                  <LottieLoader
                    loadLoop={false}
                    autoplayLoop={true}
                    source={lottieEmptyFile}
                    Lheight={150}
                    Lwidth={150}
                    subTitle={"No messages found as " + searchTerm}
                  />
                </div>
              ) : (
                renderMessagesWithDateSeparator(messagesToRender)
              )}
            </>
          )}
        </div>
      </div>
      <form onSubmit={onFormSubmit}>
        <div className="chat-control-bar chat-send-bar">
          <div className="chat-input-group">
            <span className="chat-input-icon">
              <i className="mdi mdi-message-text-outline" />
            </span>
            <textarea
              ref={messageInputRef}
              className="form-control input-date-filter remove-bottom-margin chat-control-input"
              placeholder="Type your message"
              value={message}
              rows={1}
              onChange={handleMessageChange}
              onKeyDown={handleMessageKeyDown}
            />
            <button
              className={"btn-primary sent-message-click " + (message.trim() !== '' ? "" : "disable")}
              type="submit">
              <i className="mdi mdi-send mr-1" />
              Send
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default ChatTextMessage;
